import { useState } from 'react'
import { useUserSpells } from '../hooks/useUserSpells'
import { useCollection } from '../hooks/useFirestore'
import { SPELL_SCHOOLS, SPELL_LEVELS, formatSpellLevel, getSchoolLabel } from '../lib/spellSchools'
import SpellCard from '../components/spells/SpellCard'
import './SpellbookPage.css'

export default function SpellbookPage() {
  const { spells, spellsByCharacter, userCharacters, loading } = useUserSpells()
  const { data: allCharacters } = useCollection('characters')
  const [levelFilter, setLevelFilter] = useState('all')
  const [schoolFilter, setSchoolFilter] = useState('all')
  const [characterFilter, setCharacterFilter] = useState('all')
  const [exporting, setExporting] = useState(false)

  const handleExportAll = async () => {
    setExporting(true)
    try {
      const { downloadSpellPdf } = await import('../lib/spellPdf.jsx')
      await downloadSpellPdf(filteredSpells, null, 'spellbook.pdf')
    } catch (err) {
      console.error('Failed to export PDF:', err)
    } finally {
      setExporting(false)
    }
  }

  if (loading) {
    return (
      <div className="container">
        <div className="spellbook-loading">
          <div className="loading-spinner" />
        </div>
      </div>
    )
  }

  // Apply filters
  let filteredSpells = spells
  if (levelFilter !== 'all') {
    filteredSpells = filteredSpells.filter(s => s.level === parseInt(levelFilter))
  }
  if (schoolFilter !== 'all') {
    filteredSpells = filteredSpells.filter(s => s.school === schoolFilter)
  }
  if (characterFilter !== 'all') {
    filteredSpells = filteredSpells.filter(s =>
      s.grantedCharacterIds?.includes(characterFilter)
    )
  }

  // Group by level for display
  const spellsByLevel = SPELL_LEVELS.reduce((acc, lvl) => {
    acc[lvl.value] = filteredSpells.filter(s => s.level === lvl.value)
    return acc
  }, {})

  const levelsWithSpells = SPELL_LEVELS.filter(lvl => spellsByLevel[lvl.value].length > 0)

  // Get character names for filter dropdown
  const getCharacterName = (charId) => {
    const char = allCharacters.find(c => c.id === charId)
    return char?.name || 'Unknown'
  }

  // Characters that have spells
  const charactersWithSpells = userCharacters.filter(
    c => spellsByCharacter.has(c.id)
  )

  return (
    <div className="container">
      <header className="spellbook-page-header">
        <div className="spellbook-title-row">
          <div>
            <h1>Spellbook</h1>
            <p className="text-secondary">
              {spells.length === 0
                ? 'No spells have been granted to your characters yet.'
                : `${spells.length} spell${spells.length !== 1 ? 's' : ''} available`}
            </p>
          </div>
          {spells.length > 0 && (
            <button
              className="btn btn-primary"
              onClick={handleExportAll}
              disabled={exporting}
            >
              {exporting ? 'Exporting...' : 'Export All to PDF'}
            </button>
          )}
        </div>
      </header>

      {spells.length > 0 && (
        <>
          {/* Filters */}
          <div className="spellbook-filters">
            <div className="filter-group">
              <span className="filter-label">Level</span>
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
              >
                <option value="all">All Levels</option>
                {SPELL_LEVELS.map(lvl => (
                  <option key={lvl.value} value={lvl.value}>
                    {lvl.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <span className="filter-label">School</span>
              <select
                value={schoolFilter}
                onChange={(e) => setSchoolFilter(e.target.value)}
              >
                <option value="all">All Schools</option>
                {SPELL_SCHOOLS.map(school => (
                  <option key={school.id} value={school.id}>
                    {school.label}
                  </option>
                ))}
              </select>
            </div>

            {charactersWithSpells.length > 1 && (
              <div className="filter-group">
                <span className="filter-label">Character</span>
                <select
                  value={characterFilter}
                  onChange={(e) => setCharacterFilter(e.target.value)}
                >
                  <option value="all">All Characters</option>
                  {charactersWithSpells.map(char => (
                    <option key={char.id} value={char.id}>
                      {char.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <span className="filter-count">
              {filteredSpells.length} spell{filteredSpells.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Spell List */}
          {filteredSpells.length === 0 ? (
            <div className="spellbook-empty">
              <p>No spells match your filters.</p>
            </div>
          ) : (
            <div className="spellbook-content">
              {levelsWithSpells.map(lvl => (
                <div key={lvl.value} className="spell-level-group">
                  <h2>{lvl.label}s</h2>
                  <div className="spell-grid">
                    {spellsByLevel[lvl.value].map(spell => (
                      <SpellCard key={spell.id} spell={spell} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {spells.length === 0 && (
        <div className="spellbook-empty-state">
          <div className="empty-icon">📖</div>
          <h2>Your Spellbook is Empty</h2>
          <p>
            Custom spells will appear here once the DM grants them to your character.
            Check back after your next session!
          </p>
        </div>
      )}
    </div>
  )
}
